import {
  createPrecompiledValidator,
  type ValidatorFunctions,
} from '@rjsf/validator-ajv8';
import AJV8PrecompiledValidator from '@rjsf/validator-ajv8/lib/precompiledValidator';

import ajvRuntimeEqual from 'ajv/dist/runtime/equal';
import {
  parseJson as ajvRuntimeparseJson,
  parseJsonNumber as ajvRuntimeparseJsonNumber,
  parseJsonString as ajvRuntimeparseJsonString,
} from 'ajv/dist/runtime/parseJson';
import ajvRuntimeQuote from 'ajv/dist/runtime/quote';
// import ajvRuntimeRe2 from 'ajv/dist/runtime/re2';
import ajvRuntimeTimestamp from 'ajv/dist/runtime/timestamp';
import ajvRuntimeUcs2length from 'ajv/dist/runtime/ucs2length';
import ajvRuntimeUri from 'ajv/dist/runtime/uri';
import * as ajvFormats from 'ajv-formats/dist/formats';
import { executeDynamicInlineScript } from '@agilelab/plugin-wb-platform';
import { JsonObject } from '@backstage/types/index';

// Utilities on this file are taken this example in rjsf documentation:
// https://rjsf-team.github.io/react-jsonschema-form/docs/version-5.24.10/usage/validation#dynamically-pre-compiling-validators

// dependencies to replace in generated code, to be provided by at runtime
const validatorsBundleReplacements: Record<string, [string, unknown]> = {
  // '<code to be replaced>': ['<variable name to use as replacement>', <runtime dependency>],
  'require("ajv/dist/runtime/equal").default': [
    'ajvRuntimeEqual',
    ajvRuntimeEqual,
  ],
  'require("ajv/dist/runtime/parseJson").parseJson': [
    'ajvRuntimeparseJson',
    ajvRuntimeparseJson,
  ],
  'require("ajv/dist/runtime/parseJson").parseJsonNumber': [
    'ajvRuntimeparseJsonNumber',
    ajvRuntimeparseJsonNumber,
  ],
  'require("ajv/dist/runtime/parseJson").parseJsonString': [
    'ajvRuntimeparseJsonString',
    ajvRuntimeparseJsonString,
  ],
  'require("ajv/dist/runtime/quote").default': [
    'ajvRuntimeQuote',
    ajvRuntimeQuote,
  ],
  // re2 by default is not in dependencies for ajv and so is likely not normally used
  // 'require("ajv/dist/runtime/re2").default': ['ajvRuntimeRe2', ajvRuntimeRe2],
  'require("ajv/dist/runtime/timestamp").default': [
    'ajvRuntimeTimestamp',
    ajvRuntimeTimestamp,
  ],
  'require("ajv/dist/runtime/ucs2length").default': [
    'ajvRuntimeUcs2length',
    ajvRuntimeUcs2length,
  ],
  'require("ajv/dist/runtime/uri").default': ['ajvRuntimeUri', ajvRuntimeUri],
  // formats
  'require("ajv-formats/dist/formats")': ['ajvFormats', ajvFormats],
};

const regexp = new RegExp(
  Object.keys(validatorsBundleReplacements)
    .map(key => key.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&'))
    .join('|'),
  'g',
);

function wrapAjvBundle(code: string) {
  return `function(${Object.values(validatorsBundleReplacements)
    .map(([name]) => name)
    .join(', ')}){\nvar exports = {};\n${code.replace(regexp, req => {
    return validatorsBundleReplacements[req][0];
  })};\nreturn exports;\n}`;
}

const windowValidatorOnLoad = '__rjsf_validatorOnLoad';
const validatorPromises = new Map<
  string,
  {
    promise: Promise<ValidatorFunctions>;
    resolve: (result: ValidatorFunctions) => void;
  }
>();

// Expose a global function to be called by the dynamically created script containing the precompiled validator code
// This will transform the precompiled code into actual in-memory validator functions and pass them back to evaluateValidator by resolving the corresponding promise
if (typeof window !== 'undefined') {
  // @ts-ignore
  window[windowValidatorOnLoad] = (
    loadedId: string,
    fn: (...args: unknown[]) => ValidatorFunctions,
    cache?: boolean,
  ) => {
    // injects runtime dependencies in the compiled validator
    const validator = fn(
      ...Object.values(validatorsBundleReplacements).map(([, dep]) => dep),
    );
    const validatorLoader = validatorPromises.get(loadedId);
    if (validatorLoader) {
      validatorLoader.resolve(validator);
      if (!cache) validatorPromises.delete(loadedId);
    } else {
      throw new Error(`Unknown validator loaded id="${loadedId}"`);
    }
  };
}

/**
 * Evaluate precompiled validator in browser using script tag
 * @param id Identifier to avoid evaluating the same code multiple times
 * @param code Code generated server side using `compileSchemaValidatorsCode`
 */
function evaluateValidator(
  id: string,
  code: string,
  options?: { cache?: boolean },
): Promise<ValidatorFunctions> {
  const maybeValidator = validatorPromises.get(id);
  if (maybeValidator) return maybeValidator.promise;
  let resolveValidator: (result: ValidatorFunctions) => void;
  const validatorPromise = new Promise<ValidatorFunctions>(resolve => {
    resolveValidator = resolve;
  });
  validatorPromises.set(id, {
    promise: validatorPromise,
    resolve: resolveValidator!,
  });

  executeDynamicInlineScript({
    scriptSrc: `window["${windowValidatorOnLoad}"]("${id}", ${wrapAjvBundle(
      code,
    )}, ${Boolean(options?.cache)})`,
  });

  return validatorPromise;
}

/**
 * Configuration for a precompiled validator entry.
 */
interface ValidatorEntry {
  /**
   * The precompiled validator code as a string, generated server-side by AJV's
   * standalone compiler. This code contains the validation logic for a specific schema.
   */
  code: string;

  /**
   * The original JSON schema object that the validator code was compiled from.
   * Used to create the final validator instance with proper schema context.
   */
  schema: JsonObject;
  /**
   * An identifier for the validator entry, used to manage and reference the entry.
   */
  id: string;
}

/**
 * Get validators from precompiled code
 * @param entries  An array of ValidatorEntry objects, each containing precompiled validator code and its corresponding schema. Given schema must match the schema used to compile the code EXACTLY, otherwise the validator may not work as expected.
 * @return A promise that resolves to an array of Validator instances created from the entries. The validators can be used in Rjsf Form
 */
export async function getValidatorsFromPrecompiledCode(
  entries: ValidatorEntry[],
) {
  return Promise.all(
    entries.map(c =>
      evaluateValidator(c.id, c.code).then(validator => {
        const precompiled = createPrecompiledValidator(
          validator,
          c.schema,
        ) as AJV8PrecompiledValidator<any, JsonObject, any>;
        return patchValidateFormData(precompiled);
      }),
    ),
  );
}

/**
 * Workaround for an RJSF bug in the precompiled validator.
 *
 * `Form` passes the *resolved* schema (dependencies already merged) to `validateFormData`,
 * but the validator was compiled against the *raw* schema. When `customValidate` is present,
 * RJSF internally calls `isValid` with item-level formData that lacks root-level keys, so
 * `ensureSameRootSchema` fails to match the resolved schema back to `this.rootSchema` and throws with some schemas featuring nested dependencies.
 *
 * We retry with `this.rootSchema` on failure. This is safe: the actual AJV validation always
 * runs against the precompiled main validator — the `schema` argument only gates the
 * `ensureSameRootSchema` identity check. We only wrap the existing method, so all other
 * behavior is preserved.
 */
function patchValidateFormData(
  precompiled: AJV8PrecompiledValidator<any, any, any>,
) {
  const original = precompiled.validateFormData.bind(precompiled);
  precompiled.validateFormData = (
    formData,
    resolvedSchema,
    customValidate,
    transformErrors,
    uiSchema,
  ) => {
    try {
      return original(
        formData,
        resolvedSchema,
        customValidate,
        transformErrors,
        uiSchema,
      );
    } catch {
      return original(
        formData,
        precompiled.rootSchema,
        customValidate,
        transformErrors,
        uiSchema,
      );
    }
  };
  return precompiled;
}
